import { useQuery } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api/index.ts";
import { Note } from "@/lib/api/notes.ts";
import { Session } from "@/lib/api/sessions.ts";

interface UpcomingEventsResponse {
  upcomingNotesWithReminders: Note[];
  upcomingSessions: Session[];
}

export interface UpcomingEvent {
  type: "session" | "note";
  id: string;
  date: string;
  title: string;
  content?: string;

  note?: Note;
  session?: Session;
}

export type UpcomingEvents = UpcomingEvent[];

export const useUpcomingEvents = (
  menteeIds?: string,
  type?: "session" | "note",
) => {
  return useQuery({
    queryKey: ["upcoming-events", menteeIds, type],
    queryFn: async () => {
      const { data } = await fetchApi<UpcomingEventsResponse>({
        url: "v2/mentor/upcoming-events",
        method: "GET",
        params: { menteeIds, type },
      });

      if (!data?.upcomingSessions || !data?.upcomingNotesWithReminders) {
        throw new Error("No data found");
      }

      const allEvents: UpcomingEvents = [];

      data.upcomingSessions.forEach((session) => {
        allEvents.push({
          type: "session",
          id: session.id,
          date: session.scheduled_time,
          title: session.title,

          session,
        });
      });

      data.upcomingNotesWithReminders.forEach((note) => {
        allEvents.push({
          type: "note",
          id: note.id,
          date: note.reminder!,
          title: note.title,
          content: note.content,
          note,
        });
      });

      const eventsForToday: UpcomingEvents = [];
      const restOfTheEvents: UpcomingEvents = [];

      allEvents.forEach((event) => {
        if (new Date(event.date).getDate() === new Date().getDate()) {
          eventsForToday.push(event);
        } else {
          restOfTheEvents.push(event);
        }
      });

      return {
        eventsForToday: eventsForToday.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        ),
        restOfTheEvents: restOfTheEvents.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        ),
      };
    },
  });
};
